import { useMemo } from 'react';
import { toast } from 'react-toastify';

import type { Address } from 'viem';

import type { DeserializedUser} from '@api';
import { WALLET_NETWORK } from '@api';

import { copyToClipboardWithToast } from '@shared/helpers/copyToClipboardWithToast';
import displayErrorToast from '@shared/helpers/displayErrorToast';

import {
  useGetSIWENonceMutation,
} from '@query/karma';
import {
  useConnectTonWalletMutation,
  useDeleteWalletMutation,
} from '@query/user';

import { useTonConnectUI } from '@tonconnect/ui-react';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

interface UseConnectTonToMagicIdProps {
  user?: DeserializedUser | null;
  isAuth: boolean;
  onSuccess?: (params: { walletAddress: Address }) => void;
}

const useManageTonWallet = ({ user, isAuth, onSuccess }: UseConnectTonToMagicIdProps) => {
  const [tonConnectUi] = useTonConnectUI();
  const { mutateAsync: getWalletNonce } = useGetSIWENonceMutation();
  const { mutateAsync: deleteWallet, isPending: isDeletingTonWallet } = useDeleteWalletMutation();
  const { mutateAsync: connectTonWallet, isPending: isConnectingTonWallet } =
    useConnectTonWalletMutation();

  const tonWallet = useMemo(() => {
    return user?.wallets?.find((wallet) => wallet.attributes.network === WALLET_NETWORK.TON);
  }, [user]);

  const tonAddress = tonWallet?.attributes?.address;

  const handleButtonClick = async () => {
    tonConnectUi.setConnectRequestParameters({ state: 'loading' });

    tonConnectUi.openModal();

    if (tonConnectUi.wallet) {
      await tonConnectUi.disconnect();
    }

    let nonce = '';

    try {
      nonce = await getWalletNonce();
    } catch (error) {
      toast.error(`Unable to retrieve ton proof. ${getErrorMessage(error)}`);

      tonConnectUi.closeModal();

      return;
    }

    tonConnectUi.setConnectRequestParameters({ state: 'ready', value: { tonProof: nonce } });

    const unsubscribe = tonConnectUi.onModalStateChange(async (state) => {
      unsubscribe();

      if (state.closeReason !== 'wallet-selected' || !tonConnectUi.wallet?.connectItems?.tonProof) {
        return;
      }

      try {
        const { walletAddress } = await connectTonWallet(tonConnectUi.wallet);

        onSuccess?.({ walletAddress });

        toast.success('TON wallet successfully connected');
      } catch (error) {
        toast.error(`Unable to connect TON wallet. ${getErrorMessage(error)}`);
      }
    });
  };

  const handleCopyClick = () => {
    if (tonAddress) {
      copyToClipboardWithToast(tonAddress);
    } else {
      displayErrorToast({ error: '' });
    }
  };

  const handleDeleteClick = async () => {
    try {
      const tonWalletId = tonWallet?.id;

      if (!tonWalletId) {
        throw new Error('Wallet not defined');
      }

      await deleteWallet(tonWalletId);

      toast.success('Wallet successfully unassigned from MagicID');
    } catch (error) {
      displayErrorToast({ error });
    }
  };

  const disabled = isConnectingTonWallet || !isAuth;

  return {
    disabled,
    handleButtonClick,
    handleCopyClick,
    handleDeleteClick,
    isConnectingTonWallet,
    isDeletingTonWallet,
    tonAddress,
    tonConnectUi,
    tonWallet,
  };
};

export default useManageTonWallet;
